import { useEffect, useState } from 'react';
import type {
  ContentSurface,
  PublicContentBlock,
} from '@opinix-cloud/shared';
import { fetchSurface, getDefaultBlocks, readCache } from './content';

/**
 * Hook used by every marketing page that reads from the CMS. Resolves
 * the freshest content available, in this order:
 *
 *   1. localStorage cache (≤5 min old) — instant first paint.
 *   2. /api/content/:surface fetch — takes over once it lands.
 *   3. Bundled defaults — ultimate fallback.
 *
 * State is never null, so consumer components don't need a loading
 * state for "the site has no copy." This is intentional: visual
 * fidelity REQUIRES that the site renders the same chrome regardless
 * of fetch outcome.
 */
export function useContentSurface(
  surface: ContentSurface,
): PublicContentBlock[] {
  const [blocks, setBlocks] = useState<PublicContentBlock[]>(
    () => readCache(surface) ?? getDefaultBlocks(surface),
  );

  useEffect(() => {
    let cancelled = false;
    void fetchSurface(surface).then((fresh) => {
      if (!cancelled) setBlocks(fresh);
    });
    return () => {
      cancelled = true;
    };
  }, [surface]);

  return blocks;
}
